import { RefObject, useLayoutEffect, useRef } from 'react';

const focusLost = () =>
  !document.activeElement || document.activeElement === document.body;

// A card that closes (Esc, outside click, a poll) while keyboard
// focus is inside it hands focus back to the button that opened it. Without
// this, focus falls to <body> and the next Tab restarts at the top of the
// page. A layout-effect cleanup runs before React removes the card's DOM, so
// `activeElement` still says whether focus was inside; a mouse user's focus
// is elsewhere and is left alone.
//
// The opener can leave in the same render as the card (a poll drops
// the entry, a resize folds the entry into "+N more").
// It is still in the DOM during the cleanup, so once the render is done focus
// goes to the opener if it stayed, else to `fallback`.
export const useReturnFocus = (
  container: RefObject<HTMLElement>,
  opener: () => HTMLElement | null,
  fallback: () => HTMLElement | null = () => null,
) => {
  const latestOpener = useRef(opener);
  latestOpener.current = opener;
  const latestFallback = useRef(fallback);
  latestFallback.current = fallback;

  useLayoutEffect(() => {
    const node = container.current;
    const openerAtMount = latestOpener.current();
    return () => {
      if (!node?.contains(document.activeElement)) return;
      const target = openerAtMount ?? latestOpener.current();
      target?.focus();
      queueMicrotask(() => {
        if (!focusLost()) return;
        const next = target?.isConnected ? target : latestFallback.current();
        if (next?.isConnected) next.focus();
      });
    };
  }, [container]);
};
